/**
 * Android uygulamasinin (siteden kurulan APK) guncellemesi.
 *
 * Uygulama icerigi zaten canli sunucudan geliyor; guncellenmesi gereken yalnizca
 * yerel kabuk (ikon, izinler, yerel eklentiler). Kurulu surumu yerel
 * `AppUpdater` eklentisi bildiriyor (bkz. `mobile/android/.../AppUpdaterPlugin`),
 * en guncel APK'yi `/api/downloads` - ikisi karsilastiriliyor.
 *
 * Android, Play Store disindan kurulan bir uygulamanin SESSIZCE guncellenmesine
 * izin vermiyor: indirme uygulama icinde, kurulum ekrani otomatik aciliyor ama
 * "Yukle"ye kullanici dokunuyor. Eklentisi olmayan eski APK'larda (1.0.1 ve
 * oncesi) ozellik gorunmuyor.
 */

import { listDownloads } from './downloads'

interface UpdaterInfo {
  versionName: string
  versionCode: number
  canInstall: boolean
}

interface UpdaterPlugin {
  getInfo(): Promise<UpdaterInfo>
  downloadAndInstall(options: { url: string }): Promise<{ status: string }>
  openInstallSettings(): Promise<void>
  addListener(
    event: 'downloadProgress',
    handler: (data: { percent: number }) => void,
  ): Promise<{ remove(): Promise<void> }>
}

interface CapacitorWithPlugins {
  isNativePlatform?: () => boolean
  getPlatform?: () => string
  Plugins?: Record<string, unknown>
}

function updater(): UpdaterPlugin | null {
  if (typeof window === 'undefined') return null
  const cap = (window as unknown as { Capacitor?: CapacitorWithPlugins }).Capacitor
  try {
    if (!cap?.isNativePlatform?.() || cap.getPlatform?.() !== 'android') return null
  } catch {
    return null
  }
  const plugin = cap.Plugins?.AppUpdater as UpdaterPlugin | undefined
  return plugin && typeof plugin.getInfo === 'function' ? plugin : null
}

/** Bu ortamda uygulama ici guncelleme var mi (eklentili Android uygulamasi). */
export function updatesSupported(): boolean {
  return updater() !== null
}

/** "1.0.10" > "1.0.9" - sayisal karsilastirma; eksik parcalar 0. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map((part) => Number.parseInt(part, 10) || 0)
  const pb = b.split('.').map((part) => Number.parseInt(part, 10) || 0)
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff > 0 ? 1 : -1
  }
  return 0
}

export interface UpdateCheck {
  current: string
  latest: string | null
  url: string | null
  sizeBytes: number
  available: boolean
}

/** Kurulu surumu en guncel APK ile karsilastirir; destek yoksa `null`. */
export async function checkForUpdate(): Promise<UpdateCheck | null> {
  const plugin = updater()
  if (!plugin) return null
  const info = await plugin.getInfo()
  const android = (await listDownloads()).find((item) => item.platform === 'android')
  const latest = android?.version ?? null
  const available = Boolean(latest && compareVersions(latest, info.versionName) > 0)
  return {
    current: info.versionName,
    latest,
    // Yerel eklenti yalnizca tam adresi kabul ediyor (bkz. ALLOWED_PREFIX).
    url: android ? new URL(android.url, window.location.origin).toString() : null,
    sizeBytes: android?.size_bytes ?? 0,
    available,
  }
}

/**
 * APK'yi indirip Android kurulum ekranini acar. Donus:
 *   - "installer_opened": kurulum ekrani acildi, kullanici "Yukle"ye dokunacak
 *   - "needs_permission": bilinmeyen kaynaktan kurulum izni ayari acildi
 */
export async function installUpdate(
  url: string,
  onProgress?: (percent: number) => void,
): Promise<string> {
  const plugin = updater()
  if (!plugin) throw new Error('unsupported')
  const listener = onProgress
    ? await plugin.addListener('downloadProgress', (data) => onProgress(data.percent))
    : null
  try {
    const result = await plugin.downloadAndInstall({ url })
    return result.status
  } finally {
    await listener?.remove()
  }
}

/** Profil menusundeki "Guncellemeleri denetle" -> genel pencereyi tetikler. */
export const CHECK_UPDATE_EVENT = 'hedef:check-update'

export function requestUpdateCheck(): void {
  window.dispatchEvent(new CustomEvent(CHECK_UPDATE_EVENT))
}
